import { useCallback, useEffect, useState } from 'react'

/**
 * Celá obrazovka přes nativní Fullscreen API.
 *
 * Původní aplikace na tohle používala DVĚ knihovny zároveň
 * (`react-full-screen` i `react-fullscreen-crossbrowser`) a volala
 * fullscreen v `componentDidMount` — tedy bez uživatelského gesta,
 * což prohlížeč odmítá. V konzoli produkce je kvůli tomu chyba
 * `requestFullscreen … can only be initiated by a user gesture`
 * a neodchycená `TypeError: Permissions check failed`.
 *
 * Tady je to pár řádků nad platformním API a spouští se výhradně
 * z uživatelského gesta.
 */

/** Safari používá webkit prefix; iOS fullscreen na běžné prvky neumí vůbec. */
interface WebkitDocument extends Document {
  webkitFullscreenElement?: Element | null
  webkitExitFullscreen?: () => Promise<void>
}

interface WebkitElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void>
}

function currentElement(): Element | null {
  const doc = document as WebkitDocument
  return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null
}

export function isFullscreenSupported(): boolean {
  if (typeof document === 'undefined') return false
  const el = document.documentElement as WebkitElement
  return typeof el.requestFullscreen === 'function' ||
    typeof el.webkitRequestFullscreen === 'function'
}

export interface Fullscreen {
  supported: boolean
  active: boolean
  toggle: () => void
}

export function useFullscreen(): Fullscreen {
  const [active, setActive] = useState(false)
  const [supported] = useState(isFullscreenSupported)

  useEffect(() => {
    const sync = (): void => setActive(currentElement() !== null)
    sync()

    document.addEventListener('fullscreenchange', sync)
    document.addEventListener('webkitfullscreenchange', sync)
    return () => {
      document.removeEventListener('fullscreenchange', sync)
      document.removeEventListener('webkitfullscreenchange', sync)
    }
  }, [])

  const toggle = useCallback(() => {
    const doc = document as WebkitDocument
    const el = document.documentElement as WebkitElement

    // Odmítnutí není chyba aplikace — uživatel prostě zůstane v okně.
    if (currentElement() === null) {
      const request = el.requestFullscreen?.bind(el) ?? el.webkitRequestFullscreen?.bind(el)
      void request?.().catch(() => undefined)
    } else {
      const exit = doc.exitFullscreen?.bind(doc) ?? doc.webkitExitFullscreen?.bind(doc)
      void exit?.().catch(() => undefined)
    }
  }, [])

  return { supported, active, toggle }
}
